import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, MapPin, Zap, Users, ShieldCheck, Cpu, ThermometerSun, DoorOpen, PanelTop, Wind, PlugZap, BadgeCheck } from "lucide-react";
import HeroCanvas from "@/components/HeroCanvas";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines, SectionHeading } from "@/components/Reveal";
import { CONTACT, SERVICES, REALISATIONS } from "@/data/content";

const SERVICE_ICONS = {
  "electricite-generale": Zap,
  domotique: Cpu,
  "pompes-a-chaleur": ThermometerSun,
  "motorisation-portail": DoorOpen,
  "volets-roulants": PanelTop,
  "bornes-recharge": PlugZap,
  vmc: Wind,
  "securite-confort": ShieldCheck,
};

const STATS = [
  { value: "03", label: "Départements couverts — 61, 14 & 72" },
  { value: "08", label: "Domaines d'expertise" },
  { value: "02", label: "Fondateurs à votre écoute" },
  { value: "100%", label: "Installations normes NF C 15-100" },
];

const WHY = [
  {
    icon: Users,
    title: "Les fondateurs en direct",
    text: "Pas de standard, pas d'intermédiaire : Eric Gautier et Mickaël Campos décrochent, conseillent et interviennent eux-mêmes.",
  },
  {
    icon: BadgeCheck,
    title: "Certifiés RGE, Qualifelec & IRVE",
    text: "Chaque installation respecte la NF C 15-100 et les référentiels en vigueur. La sécurité n'est pas une option — et vos aides sont préservées.",
  },
  {
    icon: MapPin,
    title: "Trois départements couverts",
    text: "Basés à Urou et Crennes, nous intervenons rapidement dans l'Orne (61), le Calvados (14) et la Sarthe (72). Un artisan local, joignable, réactif.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <main data-testid="home-page">
      {/* ——— HERO ——— */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden circuit-bg grain pt-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(15,52,140,0.1),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(230,0,43,0.05),transparent_50%)]" />
        <HeroCanvas className="absolute inset-0 h-full w-full" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid gap-16 lg:grid-cols-12 items-center w-full">
          <motion.div style={{ opacity: fade }} className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue"
              data-testid="hero-eyebrow"
            >
              <span className="h-px w-10 bg-brand-yellow/70" />
              SARL EG 6.1 — Argentan, Orne (61)
            </motion.p>

            <h1
              data-testid="hero-title"
              className="mt-8 font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase leading-[0.95] tracking-tight"
            >
              <MaskedLines
                delay={0.3}
                lines={[
                  <span key="a" className="text-ink">Le courant</span>,
                  <span key="b" className="text-brand-red glow-red">passe.</span>,
                  <span key="c" className="text-stroke">La qualité reste.</span>,
                ]}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-700"
              data-testid="hero-subtitle"
            >
              Électricité générale, domotique, pompes à chaleur, motorisation de portail, volets roulants,
              bornes de recharge, VMC & sécurité. Résidentiel, tertiaire, industriel : deux fondateurs,
              un savoir-faire, zéro compromis.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href={CONTACT.phoneHref}
                data-testid="hero-cta-phone"
                className="group inline-flex items-center gap-3 rounded-full bg-brand-blue px-7 py-4 text-base font-semibold text-white shadow-[0_10px_30px_rgba(15,52,140,0.35)] transition-all duration-300 hover:bg-[#0a2668] hover:-translate-y-0.5"
              >
                <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                {CONTACT.phone}
              </a>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-3 rounded-full border border-slate-300 px-7 py-4 text-base font-semibold text-ink transition-all duration-300 hover:border-brand-blue/60 hover:text-brand-blue"
              >
                Découvrir nos services
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.25 }}
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600"
              data-testid="hero-badges"
            >
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-red" /> Urou et Crennes — 61200</span>
              <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-brand-blue" /> Devis gratuit</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-red" /> RGE • Qualifelec • IRVE</span>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: yLogo }} className="hidden lg:block lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="absolute -inset-8 rounded-full bg-brand-blue/15 blur-3xl" aria-hidden="true" />
              <motion.img
                src="/assets/logo-eg.png"
                alt="Logo EG — Electricité Générale"
                data-testid="hero-logo"
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-full max-w-md rounded-2xl border border-slate-200 shadow-[0_24px_60px_rgba(15,52,140,0.18)]"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ——— STATS + CERTIFICATIONS ——— */}
      <section className="relative border-y border-slate-200 bg-[#eaf1fa]" data-testid="stats-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 gap-10 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <p className="font-display text-4xl sm:text-5xl font-black text-brand-blue glow-blue">{s.value}</p>
                <p className="mt-2 text-sm text-slate-600">{s.label}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3" data-testid="certifications-strip">
              <span className="text-xs font-mono uppercase tracking-[0.25em] text-slate-600 mr-2">Certifications</span>
              {CONTACT.certifications.map((c) => (
                <span
                  key={c}
                  data-testid={`certification-${c.toLowerCase()}`}
                  className="inline-flex items-center gap-2 rounded-full border border-brand-blue/30 bg-brand-ink px-5 py-2 text-sm font-semibold text-brand-blue"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee
        items={["Électricité Générale", "Domotique", "Pompes à Chaleur", "Climatisation", "Motorisation de Portail", "Volets Roulants", "Bornes de Recharge", "VMC", "Interphones & Vigik"]}
      />

      {/* ——— SERVICES BENTO ——— */}
      <section className="relative py-28 sm:py-36" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading
              testid="home-services-heading"
              eyebrow="Nos expertises"
              title={<>Huit métiers, <span className="text-brand-red">une seule exigence</span></>}
              description="Du tableau électrique à la borne de recharge, en résidentiel comme en tertiaire : nous couvrons tout l'écosystème électrique de votre bâtiment."
            />
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="home-services-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-brand-blue"
              >
                Tous nos services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.id];
              const wide = i === 0 || i === 3;
              return (
                <Reveal key={s.id} delay={(i % 3) * 0.08} className={wide ? "sm:col-span-2 lg:col-span-2" : ""}>
                  <Link
                    to="/services"
                    data-testid={`service-card-${s.id}`}
                    className="group relative flex h-full min-h-[190px] flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-brand-card p-7 transition-all duration-500 hover:border-brand-blue/40 hover:shadow-[0_16px_44px_rgba(15,52,140,0.12)]"
                  >
                    <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-brand-blue/10 blur-2xl transition-all duration-500 group-hover:bg-brand-blue/20" aria-hidden="true" />
                    <div className="flex items-start justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-ink border border-slate-200 text-brand-blue transition-colors duration-500 group-hover:text-brand-red">
                        <Icon className="h-6 w-6" />
                      </span>
                      <span className="font-mono text-xs text-slate-600">{s.num}</span>
                    </div>
                    <div className="mt-8">
                      <h3 className="font-display text-xl sm:text-2xl font-bold text-ink transition-colors duration-300 group-hover:text-brand-blue">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600">{s.baseline}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ——— POURQUOI EG 6.1 ——— */}
      <section className="relative border-y border-slate-200 bg-[#eaf1fa] py-28 sm:py-36 grain" data-testid="home-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            testid="home-why-heading"
            eyebrow="Pourquoi EG 6.1"
            title={<>Des artisans, <span className="text-brand-blue glow-blue">pas un call-center</span></>}
          />
          <div className="mt-16 grid gap-5 md:grid-cols-3">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div
                  data-testid={`why-card-${i}`}
                  className="group h-full rounded-2xl border border-slate-200 bg-brand-card/70 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-blue/40"
                >
                  <w.icon className="h-8 w-8 text-brand-red transition-colors duration-500 group-hover:text-brand-blue" />
                  <h3 className="mt-6 font-display text-xl font-bold text-ink">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ——— RÉALISATIONS APERÇU ——— */}
      <section className="py-28 sm:py-36" data-testid="home-realisations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading
              testid="home-realisations-heading"
              eyebrow="Sur le terrain"
              title={<>Nos réalisations <span className="text-brand-red">parlent d'elles-mêmes</span></>}
            />
            <Reveal delay={0.2}>
              <Link
                to="/realisations"
                data-testid="home-realisations-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-brand-blue"
              >
                Voir la galerie
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-5 md:grid-cols-3">
            {REALISATIONS.slice(0, 3).map((r, i) => (
              <Reveal key={r.id} delay={i * 0.1}>
                <Link
                  to="/realisations"
                  data-testid={`home-realisation-${r.id}`}
                  className="group relative block overflow-hidden rounded-2xl border border-slate-200"
                >
                  <img
                    src={r.img}
                    alt={r.title}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <h3 className="font-display text-xl font-bold text-ink">{r.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ——— CTA CONTACT ——— */}
      <section className="relative overflow-hidden border-t border-slate-200 py-28 sm:py-36 circuit-bg grain" data-testid="home-cta">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(15,52,140,0.08),transparent_60%)]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.28em] text-brand-blue">Un projet ? Un dépannage ?</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl sm:text-6xl font-black uppercase tracking-tight text-ink">
              Appelez, <span className="text-brand-red glow-red">on décroche.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base sm:text-lg text-slate-700">
              Eric ou Mickaël vous répondent directement. Devis gratuit, conseils sans engagement.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href={CONTACT.phoneHref}
                data-testid="home-cta-phone"
                className="inline-flex items-center gap-3 rounded-full bg-brand-blue px-8 py-4 text-lg font-bold text-white shadow-[0_10px_30px_rgba(15,52,140,0.35)] transition-all duration-300 hover:bg-[#0a2668] hover:-translate-y-0.5"
              >
                <Phone className="h-5 w-5" />
                {CONTACT.phone}
              </a>
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="inline-flex items-center gap-3 rounded-full border border-slate-300 px-8 py-4 text-lg font-semibold text-ink transition-colors duration-300 hover:border-brand-blue/60 hover:text-brand-blue"
              >
                Toutes nos coordonnées
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
